import { useUserStore } from '@st/user/stores/useUserStore'
import { useCurrenciesStore } from '@st/payments/stores/useCurrenciesStore'
import { provideProfileDeps } from '@st/profile/useDeps'
import { useAccountsStore } from '@st/payments/stores/useAccountsStore'
import { useBonusesCountStore } from '@st/bonuses/stores/useBonusesCountStore'
import RewardLabel from '@st/bonuses/components/ReferralBonus/parts/RewardLabel.vue'
import { useActiveBetsStore } from '@st/coupon/stores/useActiveBetsStore'
import BetHistory from '@st/coupon/components/BetHistory/BetHistory.vue'
import MBetHistory from '@st/coupon/components/BetHistory/MBetHistory.vue'
import { useWebPushStore } from '@st/pwa/stores/useWebPushStore'
import { use2FaStore } from '@st/user/stores/use2FaStore'
import { useKycLevel } from '@st/user/composables/useKycLevel'
import { usePasswordValidator } from '@st/user/composables/usePasswordValidator'
import { useGoogleRedirect } from '@st/user/composables/useGoogleRedirect'
import { useUser2FaChannels } from '@st/user/composables/useUser2FaChannels'
import CasinoBetsHistory from '@st/casino/components/CasinoBetsHistory/CasinoBetsHistory.vue'
import { usePromotions } from '@st/promotions/composables/usePromotions'
import { useQuestsStore } from '@st/quests/stores/useQuestsStore'
import { useVipClub } from '@st/vip-club/composables/useVipClub'
import VipClubBadge from '@st/vip-club/components/VipClubBadge/VipClubBadge.vue'

export default defineNuxtPlugin({
  name: 'profileDeps',
  dependsOn: [
    'paymentsDeps',
    'casinoDeps',
    'bonusesDeps',
    'questsDeps',
    'vipClubDeps',
  ],
  setup: () => {
    const userStore = useUserStore()
    const { isAuthenticated, user, userSettings } = storeToRefs(userStore)
    const {
      logout,
      updateUserSettings,
      refreshUserData,
      getAuthProvidersList,
    } = userStore

    const currencyStore = useCurrenciesStore()
    const { getCurrencyById } = currencyStore

    const accountsStore = useAccountsStore()
    const { activeAccount } = storeToRefs(accountsStore)

    const webPushStore = useWebPushStore()
    const { init: initWebPush, webPushTurnOn } = webPushStore

    const { claimableRewardsCounter, activeQuestsCounter } =
      storeToRefs(useQuestsStore())

    const { locale } = useNuxtApp().$i18n
    const { isEnableNotifications, hasSubscription, isDisabledNotifications } =
      storeToRefs(webPushStore)
    const { bonusesCount } = storeToRefs(useBonusesCountStore())
    const { betCounter, isReady: isReadyActiveBets } =
      storeToRefs(useActiveBetsStore())
    const twoFaStore = use2FaStore()
    const { hasTelegram, hasEmail } = storeToRefs(twoFaStore)
    const { kycLevelName } = useKycLevel()
    const { passwordValidators } = usePasswordValidator()
    const { hasTelegramProvider, hasEmailProvider, googleProvider, providers } =
      useUserAuthProviders()
    const { handleGoogleRedirect } = useGoogleRedirect()
    const {
      emailChannel,
      telegramChannel,
      activeChannel,
      isReady: isReadyChannels,
      fetchChannels,
      setActiveChannel,
    } = useUser2FaChannels()
    const { isEnabledPromotions } = usePromotions()
    const {
      backgroundMobileColor,
      userLevelTitle,
      nextLevelTitle,
      levelIcon,
      progressInCurrency,
      progressPercent,
    } = useVipClub()
    provideProfileDeps({
      initWebPush,
      webPushTurnOn,
      getCurrencyIconById: (id: number) => getCurrencyById(id)?.icon,
      getCurrencyCodeById: (id: number) => getCurrencyById(id)?.code,
      logout,
      getAuthProvidersList,
      updateUserSettings,
      refreshUserData,
      handleGoogleRedirect: async (returnUrl?: string) => {
        await handleGoogleRedirect(
          { language: locale.value },
          '/site/api/user/auth/google/redirect',
          returnUrl,
        )
      },
      fetchChannels,
      setActiveChannel,
      isEnableNotifications,
      isDisabledNotifications,
      hasSubscription,
      bonusesCount,
      betCounter,
      isReadyActiveBets,
      activeAccount,
      user,
      userSettings,
      isAuthenticated,
      hasTelegram,
      hasEmail,
      kycLevelName,
      passwordValidators,
      hasTelegramProvider: computed(() => !!hasTelegramProvider.value),
      hasEmailProvider: computed(() => !!hasEmailProvider.value),
      googleProvider: computed(() => googleProvider.value),
      isOnlyOneProvider: computed(() => providers.value.length === 1),
      isEmailChannel: computed(() => !!emailChannel.value),
      isTelegramChannel: computed(() => !!telegramChannel.value),
      isReadyChannels,
      activeChannel,
      isEnabledPromotions,
      questsClaimableRewards: claimableRewardsCounter,
      RewardLabel,
      BetHistory,
      MBetHistory,
      CasinoBetsHistory,
      VipClubBadge,
      backgroundMobileColor,
      userLevelTitle,
      nextLevelTitle,
      progressInCurrency,
      progressPercent,
      levelIcon,
      activeQuestsCounter,
    })
  },
})
